/**
 * The browser as the screen (Solo / Fiesta, docs/13 §2, §5.5) and the phone as a remote (companion,
 * §5.4). Mounted on every web route, so it stays light: Firebase's second app, the player and the
 * overlays are all separate chunks, fetched when a session exists and not for visiting the Home.
 */
import {
  AkpError,
  DEFAULT_TONE,
  type MembersNode,
  type SessionMeta,
  type TvState,
} from '@akpl/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@tanstack/react-router';
import {
  createContext,
  lazy,
  type ReactNode,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../../api';
import { useAppConfig } from '../../appConfig';
import { useAuth } from '../../auth';
import { ME_QUERY_KEY, useMe } from '../../hooks/useMe';
import { type QueueEntry, useSession } from '../../hooks/useSession';
import { useStore } from '../../store';
import { setHostTokenProvider, webApi, type WebSessionMode } from '../api';
import type { EngineEvent, PlayerEngine } from '../player/engine';
import { useWebUi, type WebSong } from '../store';
import { webDeviceId } from './deviceId';
import { signInHost, signOutHost } from './hostClient';
import { HostSession, type HostState, TakeoverRequiredError } from './hostSession';
import { useReconnecting, useScreenSilent } from './liveness';
import { type PendingNotice, useNotices } from './notices';

const loadOverlays = () => import('./SessionOverlays');
const SessionOverlays = lazy(() => loadOverlays().then((m) => ({ default: m.SessionOverlays })));

export interface WebSessionLive {
  meta: SessionMeta | null;
  /** The queue, sorted. `null` until the first snapshot. */
  items: QueueEntry[];
  members: MembersNode | null;
  tvState: TvState | null;
  connected: boolean;
  /** E-25. */
  reconnecting: boolean;
  /** E-22: not yet seen. */
  notices: PendingNotice[];
}

export type EnsureResult = 'ready' | 'takeover' | 'failed';

export interface WebSessionApi {
  mode: WebSessionMode | null;
  sid: string | null;
  joinCode: string | null;
  memberCount: number;
  /** A session is being created or recovered. */
  starting: boolean;
  live: WebSessionLive;
  /** The player of this session, once its chunk arrived. */
  engine: PlayerEngine | null;
  /** E-26. */
  takeoverRequired: boolean;
  confirmTakeover(): Promise<void>;
  cancelTakeover(): void;
  /** E-23. */
  confirmCloseParty: boolean;
  closeParty(): Promise<void>;
  cancelCloseParty(): void;
  /** E-24. */
  ended: boolean;
  acknowledgeEnded(): Promise<void>;
  /** Switches the top-bar selector: opens W-07a, or E-23 when closing a party. */
  setMode(mode: WebSessionMode): Promise<void>;
  /** Solo session for `/cantar`. `takeover`: E-26 is up and the user decides. */
  ensureSolo(): Promise<EnsureResult>;
  /** `silent`: "Cantar todas" adds a whole list, not a notice per song. */
  addToQueue(song: WebSong, tone?: string, options?: { silent?: boolean }): Promise<void>;
  markNoticeSeen(id: string): void;
  /** Logout: closes `akp-host` (§5.9). */
  shutdown(): Promise<void>;
}

export interface CompanionApi {
  /** True while this device is a guest of a party (§5.4). */
  active: boolean;
  sid: string | null;
  joinCode: string | null;
  live: Pick<WebSessionLive, 'meta' | 'items' | 'members' | 'tvState' | 'connected'>;
  /** ME-11. */
  ended: boolean;
  /** ME-13. */
  screenSilent: boolean;
  /** ME-12: the title of the song just refused. */
  blockedSong: string | null;
  dismissBlocked(): void;
  /** `POST /sessions/join` answered: this phone is in the party from here on. */
  markJoined(sid: string, joinCode: string): void;
  addToQueue(song: WebSong, tone?: string): Promise<void>;
  leave(): Promise<void>;
  /** ME-11 dismissed. */
  acknowledgeEnded(): void;
}

const noop = async () => undefined;

const EMPTY_LIVE: WebSessionLive = {
  meta: null,
  items: [],
  members: null,
  tvState: null,
  connected: false,
  reconnecting: false,
  notices: [],
};

const INERT_SESSION: WebSessionApi = {
  mode: null,
  sid: null,
  joinCode: null,
  memberCount: 0,
  starting: false,
  live: EMPTY_LIVE,
  engine: null,
  takeoverRequired: false,
  confirmTakeover: noop,
  cancelTakeover: () => undefined,
  confirmCloseParty: false,
  closeParty: noop,
  cancelCloseParty: () => undefined,
  ended: false,
  acknowledgeEnded: noop,
  setMode: noop,
  ensureSolo: async () => 'failed',
  addToQueue: noop,
  markNoticeSeen: () => undefined,
  shutdown: noop,
};

const INERT_COMPANION: CompanionApi = {
  active: false,
  sid: null,
  joinCode: null,
  live: EMPTY_LIVE,
  ended: false,
  screenSilent: false,
  blockedSong: null,
  dismissBlocked: () => undefined,
  markJoined: () => undefined,
  addToQueue: noop,
  leave: noop,
  acknowledgeEnded: () => undefined,
};

const WebSessionContext = createContext<WebSessionApi>(INERT_SESSION);
const CompanionContext = createContext<CompanionApi>(INERT_COMPANION);

function browserStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Set by the mounted provider: what an engine event does needs `t` and the router. */
let engineEventHandler: (event: EngineEvent) => void = () => undefined;

let hostSession: HostSession | null = null;

/** One per page load: StrictMode and a remounted layout must find the same session. */
function getHostSession(config: ReturnType<typeof useAppConfig>): HostSession {
  hostSession ??= new HostSession({
    deviceId: () => webDeviceId(),
    createSession: (body) => webApi.createSession(body),
    currentSession: (deviceId) => webApi.currentSession(deviceId),
    patchSession: (sid, mode) => webApi.patchSession(sid, mode),
    signIn: (customToken) => signInHost(config.firebase, customToken),
    signOut: () => signOutHost(),
    setTokenProvider: setHostTokenProvider,
    loadEngine: async (sid, client) => {
      const { createEngine } = await import('../player/runtime');
      return createEngine(sid, client, { hlsEngine: config.hlsEngine });
    },
    onEngineEvent: (event) => engineEventHandler(event),
    storage: browserStorage(),
  });
  return hostSession;
}

/** Test helper. */
export function resetHostSessionForTests(): void {
  hostSession = null;
}

const INITIAL_HOST_STATE: HostState = {
  status: 'idle',
  sid: null,
  joinCode: null,
  mode: null,
  client: null,
  engine: null,
  takeover: null,
  ended: false,
};

function useCompanionState(queryClient: ReturnType<typeof useQueryClient>) {
  const { t } = useTranslation(['web', 'common']);
  const auth = useAuth();
  const me = useMe();
  const showToast = useWebUi((s) => s.showToast);
  const [joined, setJoined] = useState<{ sid: string; joinCode: string } | null>(null);
  const [left, setLeft] = useState<string | null>(null);
  const [blockedSong, setBlockedSong] = useState<string | null>(null);

  const fromMe =
    auth.user && me.data?.activeSession?.role === 'guest' ? me.data.activeSession.id : null;
  const sid = joined?.sid ?? (fromMe !== left ? fromMe : null);
  const live = useSession(sid);
  // §5.5: closing the party removes the guests, and they find out by losing read access.
  const partyEnded = live.ended || live.revoked;
  const screenSilent = useScreenSilent(live.tvState, live.connected, Boolean(sid) && !partyEnded);

  // `useQueueActions` (the same queue calls `/app` makes) reads the sid from the store.
  useEffect(() => {
    if (!sid) return undefined;
    useStore.getState().setSid(sid);
    return () => useStore.getState().setSid(null);
  }, [sid]);

  const forgetMe = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY }),
    [queryClient],
  );

  const clear = useCallback(() => {
    setLeft(sid);
    setJoined(null);
    setBlockedSong(null);
    void forgetMe();
  }, [forgetMe, sid]);

  return useMemo<CompanionApi>(
    () => ({
      active: Boolean(sid),
      sid,
      joinCode: live.meta?.joinCode ?? joined?.joinCode ?? null,
      live,
      ended: partyEnded,
      screenSilent,
      blockedSong,
      dismissBlocked: () => setBlockedSong(null),
      markJoined: (nextSid, joinCode) => {
        setJoined({ sid: nextSid, joinCode });
        setLeft(null);
        void forgetMe();
      },
      addToQueue: async (song, tone) => {
        if (!sid) return;
        try {
          await webApi.enqueue(sid, { songId: song.id, tone: tone ?? DEFAULT_TONE });
          showToast({ message: t('web:party.toast.addedGuest', { title: song.title }) });
        } catch (error) {
          const code = error instanceof AkpError ? error.code : '';
          if (code === 'feature_required') setBlockedSong(song.title);
          else if (code === 'duplicate_in_queue') {
            showToast({ message: t('web:party.toast.duplicate', { title: song.title }) });
          } else {
            showToast({ message: t('web:party.toast.addFailed'), tone: 'error' });
          }
        }
      },
      leave: async () => {
        if (sid) await api.sessionLeave({ params: { sid } }).catch(() => undefined);
        clear();
      },
      acknowledgeEnded: clear,
    }),
    [blockedSong, clear, forgetMe, joined, live, partyEnded, screenSilent, showToast, sid, t],
  );
}

function pendingOverlay(
  host: HostState,
  closing: boolean,
  session: WebSessionApi,
  companion: CompanionApi,
) {
  return (
    Boolean(host.takeover) ||
    closing ||
    host.ended ||
    session.live.reconnecting ||
    session.live.notices.length > 0 ||
    companion.ended ||
    companion.blockedSong !== null
  );
}

export function WebSessionProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation(['web', 'common']);
  const config = useAppConfig();
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showToast = useWebUi((s) => s.showToast);
  const openOverlay = useWebUi((s) => s.openOverlay);

  const host = getHostSession(config);
  const state = useSyncExternalStore(host.subscribe, host.getState, () => INITIAL_HOST_STATE);
  const [closing, setClosing] = useState(false);

  const live = useSession(state.sid, state.client);
  const { notices, markSeen } = useNotices(state.sid, state.client);
  const reconnecting = useReconnecting(live.connected, Boolean(state.sid) && !state.ended);
  const companion = useCompanionState(queryClient);

  // E-25 shows up precisely when its chunk can no longer be fetched: it is here before the network
  // goes, or the failed import takes the whole page, and the song, down with it.
  const inSession = Boolean(state.sid) || companion.active;
  useEffect(() => {
    if (inSession) void loadOverlays().catch(() => undefined);
  }, [inSession]);

  // Log in, log out, another account: the screen session belongs to the account that opened it.
  const uid = auth.isAccount ? (auth.user?.uid ?? null) : null;
  const previousUid = useRef(uid);
  useEffect(() => {
    if (previousUid.current && previousUid.current !== uid) void host.shutdown();
    previousUid.current = uid;
    if (uid) void host.recover(uid);
  }, [host, uid]);

  useEffect(() => {
    engineEventHandler = (event) => {
      if (event.type === 'toneFallback') {
        showToast({ message: t('web:player.tone.fallback') });
      } else if (event.type === 'premiumRequired') {
        openOverlay({ kind: 'paywall' });
      } else {
        host.markEnded();
      }
    };
  }, [host, openOverlay, showToast, t]);

  // E-24: closed from anywhere, or out of time with nobody to write it.
  const expiresAt = live.meta?.expiresAt;
  useEffect(() => {
    if (!state.sid) return undefined;
    if (live.ended) {
      host.markEnded();
      return undefined;
    }
    if (!expiresAt) return undefined;
    // `setTimeout` overflows past ~24.8 days; a session lasts hours.
    const timer = setTimeout(() => host.markEnded(), Math.max(0, expiresAt - Date.now()));
    return () => clearTimeout(timer);
  }, [expiresAt, host, live.ended, state.sid]);

  // A Solo session has nobody to tell: it ends quietly and the next song opens another one.
  useEffect(() => {
    if (state.ended && state.mode !== 'party') void host.shutdown();
  }, [host, state.ended, state.mode]);

  const ensureOrToast = useCallback(
    async (mode: WebSessionMode): Promise<EnsureResult> => {
      try {
        // A page's effect runs before this provider's, so on a reload of `/cantar` the page would
        // ask for a new session ahead of `recover`. §5.5: a reload recovers it with
        // `GET /web/sessions/current`. Idempotent, and queued ahead of the `ensure`.
        if (uid) void host.recover(uid);
        await host.ensure(mode);
        return 'ready';
      } catch (error) {
        if (error instanceof TakeoverRequiredError) return 'takeover';
        showToast({ message: t('web:party.toast.startFailed'), tone: 'error' });
        return 'failed';
      }
    },
    [host, showToast, t, uid],
  );

  // W-07a → W-07 "pasa sola" (§5.5), also when the host went to look for the first song.
  const { pathname } = useLocation();
  const songsWaiting = live.items.some((item) => item.status !== 'played');
  const hadSongs = useRef(false);
  useEffect(() => {
    if (state.mode === 'party' && songsWaiting && !hadSongs.current && pathname !== '/fiesta') {
      void navigate({ to: '/fiesta' });
    }
    hadSongs.current = songsWaiting;
  }, [navigate, pathname, songsWaiting, state.mode]);

  const members = live.members;
  const session = useMemo<WebSessionApi>(
    () => ({
      mode: state.mode,
      sid: state.sid,
      joinCode: live.meta?.joinCode ?? state.joinCode,
      memberCount: members ? Object.keys(members).length : 0,
      starting: state.status === 'starting',
      live: {
        meta: live.meta,
        items: live.items,
        members,
        tvState: live.tvState,
        connected: live.connected,
        reconnecting,
        notices,
      },
      engine: state.engine,
      takeoverRequired: Boolean(state.takeover),
      confirmTakeover: async () => {
        const wanted = state.takeover;
        try {
          await host.confirmTakeover();
          if (wanted === 'party') void navigate({ to: '/fiesta' });
        } catch {
          showToast({ message: t('web:party.toast.startFailed'), tone: 'error' });
        }
      },
      cancelTakeover: () => host.cancelTakeover(),
      confirmCloseParty: closing,
      closeParty: async () => {
        setClosing(false);
        try {
          await host.closeParty();
          void navigate({ to: '/' });
        } catch {
          showToast({ message: t('web:party.toast.closeFailed'), tone: 'error' });
        }
      },
      cancelCloseParty: () => setClosing(false),
      ended: state.ended,
      acknowledgeEnded: () => host.shutdown(),
      setMode: async (mode) => {
        if (mode === 'solo') {
          if (state.mode === 'party') setClosing(true);
          return;
        }
        // Navigating waits for the PATCH: leaving `/cantar` while still in Solo would end the song
        // that "Invitar amigos" is meant to carry into the party.
        if ((await ensureOrToast('party')) === 'ready') void navigate({ to: '/fiesta' });
      },
      ensureSolo: () => ensureOrToast('solo'),
      addToQueue: async (song, tone, options) => {
        // From the Home with no session yet: this is the moment to spend one (§8 riesgo 6).
        if (!host.getState().sid && (await ensureOrToast('solo')) !== 'ready') return;
        const target = host.getState().sid;
        if (!target) return;
        try {
          await webApi.enqueue(target, { songId: song.id, tone: tone ?? DEFAULT_TONE });
          if (options?.silent) return;
          showToast({
            message: t('web:player.toast.added', { title: song.title }),
            action: {
              label: t('web:player.toast.viewQueue'),
              run: () => {
                void navigate({ to: host.getState().mode === 'party' ? '/fiesta' : '/cantar' });
              },
            },
          });
        } catch (error) {
          const code = error instanceof AkpError ? error.code : '';
          if (code === 'feature_required') openOverlay({ kind: 'paywall', song });
          else if (code === 'duplicate_in_queue') {
            showToast({ message: t('web:party.toast.duplicate', { title: song.title }) });
          } else if (code === 'session_expired') host.markEnded();
          else showToast({ message: t('web:party.toast.addFailed'), tone: 'error' });
        }
      },
      markNoticeSeen: markSeen,
      shutdown: () => host.shutdown(),
    }),
    [
      closing,
      ensureOrToast,
      host,
      live,
      markSeen,
      members,
      navigate,
      notices,
      openOverlay,
      reconnecting,
      showToast,
      state,
      t,
    ],
  );

  return (
    <WebSessionContext.Provider value={session}>
      <CompanionContext.Provider value={companion}>
        {children}
        {pendingOverlay(state, closing, session, companion) ? (
          <Suspense fallback={null}>
            <SessionOverlays />
          </Suspense>
        ) : null}
      </CompanionContext.Provider>
    </WebSessionContext.Provider>
  );
}

export function useWebSession(): WebSessionApi {
  return useContext(WebSessionContext);
}

export function useCompanion(): CompanionApi {
  return useContext(CompanionContext);
}
